import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import { requestTickets, setUser, requestInvoice } from "@/redux/ducks/user";
import { ReducersTypes } from "@/redux/reducers/Models";
import { getIsValidString } from "@/utils/functions";
import { DebtsByInvoice, getFormattedDebtsByInvoice, getFormattedTickets, Tickets } from "@/utils/transform";
import useRedirect from "../../hooks/useRedirect";
import TicketView from "./view";

export default function Ticket() {
  useRedirect("ticket");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: ReducersTypes & {
    userReducer: ReducersTypes['userReducer'] & {
      tickets: {
        date: string;
        price: string;
        id: string;
        status: string;
      }[];
    }
  }) => state.userReducer);

  const [isLoading, setIsLoading] = useState(false);
  const [errorTickets, setErrorTickets] = useState(false);
  const [errorInvoice, setErrorInvoice] = useState(false);

  const goBack = () => navigate("/");

  const generatePDF = ({
    name,
    invoiceId,
    invoiceDate,
    price,
    debts,
  }: {
    name: string,
    invoiceId: string,
    invoiceDate: string,
    price: string,
    debts: {
      plate: string;
      date: string;
      price: string;
      owner: string;
    }[];
  }) => {
    const doc = new jsPDF();

    const pageHeight = doc.internal.pageSize.height;
    const marginTop = 10;
    let currentY = 50;

    const logo = "/pdfimage.png";
    doc.addImage(logo, "PNG", 85, marginTop, 28.8, 20.64);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Confirmação de pagamento de Ticket", 105, marginTop + 30, { align: "center" });

    currentY = marginTop + 40;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text("Olá, " + name, 20, currentY);
    doc.text(
      "Seu pagamento de R$ " + price + " foi realizado com sucesso. Confira os detalhes a seguir.",
      20,
      currentY + 8
    );

    currentY += 20;
    doc.setFont("helvetica", "bold");
    doc.text("Dados da transação #" + invoiceId, 20, currentY);

    currentY += 5;
    const addTransactionRow = (label: string, value: string) => {
      if (currentY > pageHeight - 20) {
        doc.addPage();
        currentY = marginTop;
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.rect(20, currentY, 170, 8);
      doc.text(label, 22, currentY + 5);
      doc.text(value, 190 - 5, currentY + 5, { align: "right" });
      currentY += 8;
    };

    addTransactionRow("Data:", invoiceDate);
    addTransactionRow("Valor:", "R$ " + price);

    currentY += 10;
    doc.setFont("helvetica", "bold");
    doc.text("Detalhes do pagamento", 20, currentY);

    currentY += 10;
    debts?.forEach(item => {
      if (currentY > pageHeight - 40) {
        doc.addPage();
        currentY = marginTop + 10;
      }

      addTransactionRow("Placa:", item?.plate || "N/A");
      addTransactionRow("Data:", item?.date || "N/A");
      addTransactionRow("Valor:", "R$ " + (item?.price || "0,00"));
      addTransactionRow("Nome Concessionária:", item?.owner || "Detalhe se houver");
      currentY += 10;
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "Recibo gerado em " + new Date().toLocaleString("pt-BR"),
      20,
      pageHeight - 10
    );

    doc.save(invoiceId + ".pdf");
  };

  const handleRequestInvoice = (invoice: {
    date: string;
    price: string;
    id: string;
    status: string;
  }, success: boolean, code?: string | DebtsByInvoice[]) => {
    setIsLoading(false);

    if (!success) {
      setErrorInvoice(true);
      return;
    }

    // open invoice
    generatePDF({
      name: "",
      invoiceId: invoice.id,
      invoiceDate: invoice.date,
      price: invoice.price,
      debts: getFormattedDebtsByInvoice(code! as DebtsByInvoice[]),
    });
  }

  const getInvoice = (invoice: {
    date: string;
    price: string;
    id: string;
    status: string;
  }) => {
    if (isLoading) return;
    
    setIsLoading(true);
    setErrorTickets(false);
    setErrorInvoice(false);

    dispatch(
      requestInvoice({
        invoiceId: getIsValidString(invoice?.id) ? invoice?.id : user?.invoiceId as string,
        callback: (success, code) => handleRequestInvoice(invoice, success, code),
      })
    );
  }

  const handleRequestTickets = (success: boolean, code?: string | Tickets[]) => {
    setIsLoading(false);

    if (!success) {
      setErrorTickets(true);
      dispatch(
        setUser({
          tickets: [],
        })
      );
      return;
    }

    dispatch(
      setUser({
        tickets: getFormattedTickets(code! as Tickets[]),
      })
    );
  }

  const getTickets = () => {
    if (isLoading) return;

    setIsLoading(true);
    setErrorTickets(false);
    setErrorInvoice(false);

    dispatch(
      requestTickets({
        callback: handleRequestTickets,
      })
    );
  }

  useEffect(() => {
    getTickets();
  }, []);

  return <TicketView
    tickets={user?.tickets}
    openInvoice={getInvoice}
    goBack={goBack}
    isLoading={isLoading}
    errorTickets={errorTickets}
    errorInvoice={errorInvoice}
    setErrorTickets={setErrorTickets}
    tryAgain={() => {
      setErrorTickets(false);
      getTickets();
    }}
    setErrorInvoice={setErrorInvoice}
  />;
}
